import React, { createContext, useContext, useMemo, useRef, useState, useEffect, useCallback } from "react";

const UIStateContext = createContext(null);

// 공용 스택 컨트롤러 (바텀시트/모달 둘 다 이걸로 관리)
function useStackController() {
  const stackRef = useRef([]);
  const [open, setOpen] = useState(false);

  const register = useCallback((fn) => {
    // closersRef.current.add(fn);
    // setOpen(closersRef.current.size > 0);
    // 같은 함수여도 '독립 엔트리'로 push
    stackRef.current.push(fn);
    setOpen(stackRef.current.length > 0);
    return () => {
      const arr = stackRef.current;
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i] === fn) {
          arr.splice(i, 1); // 한 개만 제거
          break;
        }
      }
      setOpen(stackRef.current.length > 0);
    };
  }, []);

  const closeTop = useCallback(() => {
    const arr = stackRef.current;
    if (arr.length === 0) return false;

    // 🚩 먼저 pop으로 스택에서 제거(애니메이션/언마운트 레이스 방지)
    const top = arr.pop();
    setOpen(arr.length > 0);

    try { top(); } catch (_) {}
    return true;
  }, []);

  return { open, register, closeTop };
}

export function UIStateProvider({ children }) {
  // 바텀시트/모달 공용 컨트롤
  const bottom = useStackController();
  const modal = useStackController();

  // 뒤로가기/차단 상태
  const [needsConfirm, setNeedsConfirm] = useState(false);

  // 🔒 바텀시트/모달 열리면 배경 스크롤 잠그고, 닫히면 복원
  useEffect(() => {
      const lock = bottom.open || modal.open || needsConfirm;
      const body = document.body;
      const html = document.documentElement;

      if (lock) {
          // 1) 현재 스크롤 Y 저장
          const scrollY = window.scrollY || html.scrollTop || 0;

          // 2) 최초 잠금일 때만 스냅샷 저장
          if (!body.dataset._lock_snapshot) {
              const snapshot = {
                  position: body.style.position,
                  top: body.style.top,
                  left: body.style.left,
                  right: body.style.right,
                  width: body.style.width,
                  overflow: body.style.overflow,
                  overscrollBehavior: body.style.overscrollBehavior,
                  paddingRight: body.style.paddingRight,
              };
              body.dataset._lock_snapshot = JSON.stringify(snapshot);

              // 3) 스크롤바 보정(레이아웃 흔들림 방지)
              const hasVScroll = window.innerWidth > html.clientWidth;
              const scrollbarWidth = hasVScroll ? (window.innerWidth - html.clientWidth) : 0;
              if (scrollbarWidth > 0) {
                  body.style.paddingRight = `${scrollbarWidth}px`;
              }

              // 4) 잠금 적용
              body.style.position = "fixed";
              body.style.top = `-${scrollY}px`;
              body.style.left = "0";
              body.style.right = "0";
              body.style.width = "100%";
              body.style.overflow = "hidden";
              body.style.overscrollBehavior = "contain"; // iOS 체이닝/탄성 완화
          }
      } else {
          // 🔓 복원 (스냅샷이 있을 때만)
          if (body.dataset._lock_snapshot) {
              const snapshot = JSON.parse(body.dataset._lock_snapshot);
              delete body.dataset._lock_snapshot;

              // 현재 top에서 정확한 복구값 계산(음수 문자열 안전)
              const y = Math.abs(parseInt(body.style.top || "0", 10)) || 0;

              body.style.position = snapshot.position || "";
              body.style.top = snapshot.top || "";
              body.style.left = snapshot.left || "";
              body.style.right = snapshot.right || "";
              body.style.width = snapshot.width || "";
              body.style.overflow = snapshot.overflow || "";
              body.style.overscrollBehavior = snapshot.overscrollBehavior || "";
              body.style.paddingRight = snapshot.paddingRight || "";

              window.scrollTo(0, y);
          }
      }
  }, [bottom.open, modal.open, needsConfirm]);

  // 페이지 뒤로가기 핸들러
  const pageBackHandlersRef = useRef(new Set());
  const registerPageBackHandler = useCallback((fn) => {
    pageBackHandlersRef.current.add(fn);
    return () => pageBackHandlersRef.current.delete(fn);
  }, []);
  const triggerPageBackHandler = useCallback(() => {
    const arr = Array.from(pageBackHandlersRef.current);
    if (arr.length === 0) return false;
    const top = arr[arr.length - 1];
    try { top(); } catch (_) {}
    return true;
  }, []);

  const value = useMemo(() => ({
    // 상태
    bottomSheetOpen: bottom.open,
    modalOpen: modal.open,
    needsConfirm,

    // 제어
    setNeedsConfirm,

    // 등록/해제 API
    registerBottomSheetCloser: bottom.register,
    registerModalCloser: modal.register,

    // 닫기 트리거 (원래 이름 유지)
    closeTopBottomSheet: bottom.closeTop,
    closeTopModal: modal.closeTop,

    // 편의 alias (기존 코드 호환)
    closeBottomSheet: bottom.closeTop,
    closeModal: modal.closeTop,

    // 페이지 뒤로 핸들러
    registerPageBackHandler,
    triggerPageBackHandler,

    // 편의 플래그
    hasBlockingUI: bottom.open || modal.open || needsConfirm,
  }), [bottom.open, modal.open, needsConfirm, bottom.register, modal.register, bottom.closeTop, modal.closeTop, registerPageBackHandler, triggerPageBackHandler]);

  return <UIStateContext.Provider value={value}>{children}</UIStateContext.Provider>;
}

export function useUIState() {
  const ctx = useContext(UIStateContext);
  if (!ctx) throw new Error("useUIState must be used within UIStateProvider");
  return ctx;
}
